const CONTEXT_IMAGES = {
  pizza: "https://static.prod-images.emergentagent.com/jobs/efb5525d-e484-4894-a0d3-6f8b4f2b6938/images/255de3729f97f91a3df5034d8729849e09518221dba7f6de7b89a4422a2178f1.jpeg",
  fruits: "https://static.prod-images.emergentagent.com/jobs/efb5525d-e484-4894-a0d3-6f8b4f2b6938/images/1f258ee54e799e414878d84a572c0065ca71c38bd286ba0db347900438171ce9.jpeg",
  coins: "https://static.prod-images.emergentagent.com/jobs/efb5525d-e484-4894-a0d3-6f8b4f2b6938/images/0f7b812936ee8e6d129f29168c4ceb727045b945dd02ab57888a0a62c4bd6bb8.jpeg",
  blocks: "https://static.prod-images.emergentagent.com/jobs/efb5525d-e484-4894-a0d3-6f8b4f2b6938/images/9bc348713b0e507d420e9ad4c2ad51feeefb912e99e51163ffcbb30406ef6d72.jpeg",
};

const PLACE_LABELS = ["Ones", "Tens", "Hundreds", "Thousands", "Ten Thousands", "Lakhs"];
const PLACE_COLORS = ["text-primary", "text-secondary", "text-success", "text-accent", "text-destructive", "text-primary"];

function NumberLine({ min, max, step = 1, highlight = [] }) {
  const ticks = [];
  for (let v = min; v <= max; v += step) ticks.push(v);
  return (
    <div className="py-4 px-2" data-testid="visual-number-line">
      <div className="relative h-10 flex items-center">
        <div className="absolute left-0 right-0 h-0.5 bg-border" />
        {ticks.map((v) => {
          const isHi = highlight.includes(v);
          const pos = ((v - min) / (max - min)) * 100;
          return (
            <div key={v} className="absolute flex flex-col items-center" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
              <div className={`w-2.5 h-2.5 rounded-full ${isHi ? "bg-primary ring-4 ring-primary/20" : "bg-muted-foreground/40"}`} />
              <span className={`text-[10px] mt-1 whitespace-nowrap ${isHi ? "font-bold text-primary" : "text-muted-foreground"}`}>{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FractionBar({ fractions = [] }) {
  return (
    <div className="py-2 space-y-3" data-testid="visual-fraction-bar">
      {fractions.map((f, i) => (
        <div key={i}>
          <div className="flex h-8 rounded-md overflow-hidden border border-border">
            {Array.from({ length: f.denominator }).map((_, seg) => (
              <div key={seg} className={`flex-1 border-r border-border last:border-r-0 ${seg < f.numerator ? "bg-primary" : "bg-muted"}`} />
            ))}
          </div>
          <p className="text-xs font-math font-semibold text-center mt-1">{f.numerator}/{f.denominator}</p>
        </div>
      ))}
    </div>
  );
}

function PlaceValue({ number }) {
  const digits = String(Math.abs(number)).split("");
  return (
    <div className="flex gap-2 justify-center py-3 flex-wrap" data-testid="visual-place-value">
      {digits.map((d, i) => {
        const placeIdx = digits.length - 1 - i;
        return (
          <div key={i} className="flex flex-col items-center bg-card border border-border rounded-lg px-3 py-2 min-w-[52px]">
            <span className={`text-xl font-math font-extrabold ${PLACE_COLORS[placeIdx % PLACE_COLORS.length]}`}>{d}</span>
            <span className="text-[9px] text-muted-foreground mt-0.5 text-center leading-tight">{PLACE_LABELS[placeIdx] || "Place"}</span>
          </div>
        );
      })}
    </div>
  );
}

function renderFactorNode(node, depth = 0) {
  return (
    <div className="flex flex-col items-center" key={`${depth}-${node.number}`}>
      <div className="w-10 h-10 rounded-full bg-primary/10 border-2 border-primary text-primary font-bold flex items-center justify-center text-sm">
        {node.number}
      </div>
      {node.children && node.children.length > 0 ? (
        <div className="flex flex-col items-center">
          <div className="w-px h-3 bg-border" />
          <div className="flex gap-6 relative">
            <div className="absolute -top-3 left-0 right-0 h-px bg-border" />
            {node.children.map((child, i) => (
              <div key={i} className="flex flex-col items-center pt-3">
                {renderFactorNode(child, depth + 1)}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FactorTree({ number, children = [] }) {
  return (
    <div className="flex justify-center py-3" data-testid="visual-factor-tree">
      {renderFactorNode({ number, children })}
    </div>
  );
}

function BalanceScale({ left, right, balanced = true }) {
  return (
    <div className="py-4" data-testid="visual-balance-scale">
      <div className="flex items-end justify-center gap-3">
        <div className="bg-primary/10 border-2 border-primary rounded-xl px-4 py-3 min-w-[80px] text-center">
          <span className="font-math font-bold text-sm">{left}</span>
        </div>
        <span className="text-lg font-bold text-muted-foreground pb-3">=</span>
        <div className="bg-secondary/15 border-2 border-secondary rounded-xl px-4 py-3 min-w-[80px] text-center">
          <span className="font-math font-bold text-sm">{right}</span>
        </div>
      </div>
      <div className="w-24 h-1 bg-foreground/70 rounded-full mx-auto mt-2" />
      <div className="w-0 h-0 mx-auto border-l-[10px] border-r-[10px] border-b-[14px] border-l-transparent border-r-transparent border-b-foreground/70" />
      {!balanced && <p className="text-center text-[10px] text-muted-foreground mt-1">not yet balanced — keep solving!</p>}
    </div>
  );
}

function DecimalGrid({ values = [] }) {
  return (
    <div className="flex gap-4 justify-center py-2 flex-wrap" data-testid="visual-decimal-grid">
      {values.map((value, vi) => {
        const shaded = Math.round(value * 100);
        return (
          <div key={vi} className="flex flex-col items-center">
            <div className="grid grid-cols-10 gap-[1px] w-24 h-24 bg-border p-[1px] rounded">
              {Array.from({ length: 100 }).map((_, i) => (
                <div key={i} className={i < shaded ? "bg-primary" : "bg-card"} />
              ))}
            </div>
            <span className="text-xs font-math font-semibold mt-1">{value}</span>
          </div>
        );
      })}
    </div>
  );
}

function ContextImage({ tag }) {
  const src = CONTEXT_IMAGES[tag];
  if (!src) return null;
  return (
    <div className="py-2 flex justify-center" data-testid="visual-context-image">
      <img src={src} alt={tag} className="w-32 h-32 object-cover rounded-xl border border-border" />
    </div>
  );
}

const SHAPE_PATHS = {
  triangle: "50,10 90,85 10,85",
  square: "15,15 85,15 85,85 15,85",
  rectangle: "10,25 90,25 90,75 10,75",
  pentagon: "50,8 90,38 75,88 25,88 10,38",
  hexagon: "50,6 88,28 88,72 50,94 12,72 12,28",
};

function Shape2D({ shape = "square", label }) {
  const isCircle = shape === "circle";
  return (
    <div className="flex flex-col items-center py-3" data-testid="visual-shape-2d">
      <svg viewBox="0 0 100 100" className="w-24 h-24">
        {isCircle ? (
          <circle cx="50" cy="50" r="42" className="fill-primary/10 stroke-primary" strokeWidth="3" />
        ) : (
          <polygon points={SHAPE_PATHS[shape] || SHAPE_PATHS.square} className="fill-primary/10 stroke-primary" strokeWidth="3" />
        )}
      </svg>
      <span className="text-xs font-semibold mt-1 capitalize">{label || shape}</span>
    </div>
  );
}

function Clock({ hour = 12, minute = 0 }) {
  const hourAngle = (hour % 12) * 30 + minute * 0.5;
  const minuteAngle = minute * 6;
  const ticks = Array.from({ length: 12 }, (_, i) => i);
  return (
    <div className="flex flex-col items-center py-3" data-testid="visual-clock">
      <svg viewBox="0 0 100 100" className="w-28 h-28">
        <circle cx="50" cy="50" r="46" className="fill-card stroke-border" strokeWidth="2" />
        {ticks.map((i) => {
          const angle = i * 30 * (Math.PI / 180);
          const x1 = 50 + 40 * Math.sin(angle);
          const y1 = 50 - 40 * Math.cos(angle);
          const x2 = 50 + 46 * Math.sin(angle);
          const y2 = 50 - 46 * Math.cos(angle);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} className="stroke-muted-foreground" strokeWidth="2" />;
        })}
        <line x1="50" y1="50" x2={50 + 24 * Math.sin(hourAngle * Math.PI / 180)} y2={50 - 24 * Math.cos(hourAngle * Math.PI / 180)}
          className="stroke-foreground" strokeWidth="4" strokeLinecap="round" />
        <line x1="50" y1="50" x2={50 + 34 * Math.sin(minuteAngle * Math.PI / 180)} y2={50 - 34 * Math.cos(minuteAngle * Math.PI / 180)}
          className="stroke-primary" strokeWidth="3" strokeLinecap="round" />
        <circle cx="50" cy="50" r="3" className="fill-primary" />
      </svg>
      <span className="text-xs font-math font-semibold mt-1">{String(hour).padStart(2, "0")}:{String(minute).padStart(2, "0")}</span>
    </div>
  );
}

function UnitLadder({ units = [] }) {
  return (
    <div className="flex items-center justify-center gap-1 py-3 flex-wrap" data-testid="visual-unit-ladder">
      {units.map((u, i) => (
        <div key={u.label} className="flex items-center gap-1">
          <div className="bg-card border border-border rounded-lg px-3 py-2 text-sm font-math font-bold">{u.label}</div>
          {i < units.length - 1 && (
            <div className="flex flex-col items-center px-1">
              <span className="text-[10px] text-muted-foreground">× {u.factor_to_next}</span>
              <span className="text-primary text-sm">→</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function VisualWidget({ op }) {
  if (!op) return null;
  if (op.type === "image") return <ContextImage tag={op.tag} />;
  const widget = op.widget;
  const params = op.params || {};
  if (widget === "number_line") return <NumberLine {...params} />;
  if (widget === "fraction_bar") return <FractionBar {...params} />;
  if (widget === "place_value") return <PlaceValue {...params} />;
  if (widget === "factor_tree") return <FactorTree {...params} />;
  if (widget === "balance_scale") return <BalanceScale {...params} />;
  if (widget === "decimal_grid") return <DecimalGrid {...params} />;
  if (widget === "shape_2d") return <Shape2D {...params} />;
  if (widget === "clock") return <Clock {...params} />;
  if (widget === "unit_ladder") return <UnitLadder {...params} />;
  return null;
}

export function ConceptVisuals({ concept }) {
  if (!concept) return null;
  const hasVisual = !!concept.visual;
  const hasImage = !!concept.context_image;
  if (!hasVisual && !hasImage) return null;
  return (
    <div className="bg-card border border-border rounded-2xl p-4 mb-4" data-testid="concept-visual-aid">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Visual Aid</p>
      {hasVisual && <VisualWidget op={{ type: "visual", widget: concept.visual.widget, params: concept.visual.params }} />}
      {hasImage && <ContextImage tag={concept.context_image} />}
    </div>
  );
}

